import * as React from 'react';
import { getNativeElementProps, useEventCallback, useId, slot } from '@fluentui/react-utilities';
import { DismissRegular } from '@fluentui/react-icons';
import { Delete, Backspace } from '@fluentui/keyboard-keys';
import { useTagGroupContext_unstable } from '../../contexts/tagGroupContext';
const tagAvatarSizeMap = {
    medium: 28,
    small: 20,
    'extra-small': 16
};
const tagAvatarShapeMap = {
    rounded: 'square',
    circular: 'circular'
};
/**
 * Create the state required to render Tag.
 *
 * The returned state can be modified with hooks such as useTagStyles_unstable,
 * before being passed to renderTag_unstable.
 *
 * @param props - props from this instance of Tag
 * @param ref - reference to root HTMLElement of Tag
 */ export const useTag_unstable = (props, ref)=>{
    const { handleTagDismiss, size: contextSize } = useTagGroupContext_unstable();
    const id = useId('fui-Tag', props.id);
    const { appearance = 'filled', disabled = false, dismissible = false, shape = 'rounded', size = contextSize, value = id } = props;
    const dismissOnClick = useEventCallback((ev)=>{
        var _props_onClick, _props;
        (_props_onClick = (_props = props).onClick) === null || _props_onClick === void 0 ? void 0 : _props_onClick.call(_props, ev);
        if (!ev.defaultPrevented) {
            var _handleTagDismiss;
            (_handleTagDismiss = handleTagDismiss) === null || _handleTagDismiss === void 0 ? void 0 : _handleTagDismiss(ev, value);
        }
    });
    const dismissOnKeyDown = useEventCallback((ev)=>{
        var _props_onKeyDown, _props;
        (_props = props) === null || _props === void 0 ? void 0 : (_props_onKeyDown = _props.onKeyDown) === null || _props_onKeyDown === void 0 ? void 0 : _props_onKeyDown.call(_props, ev);
        if (!ev.defaultPrevented && (ev.key === Delete || ev.key === Backspace)) {
            var _handleTagDismiss;
            (_handleTagDismiss = handleTagDismiss) === null || _handleTagDismiss === void 0 ? void 0 : _handleTagDismiss(ev, value);
        }
    });
    return {
        appearance,
        avatarShape: tagAvatarShapeMap[shape],
        avatarSize: tagAvatarSizeMap[size],
        disabled,
        dismissible,
        shape,
        size,
        components: {
            root: dismissible ? 'button' : 'span',
            media: 'span',
            icon: 'span',
            primaryText: 'span',
            secondaryText: 'span',
            dismissIcon: 'span'
        },
        root: slot.always(getNativeElementProps('button', {
            ref,
            ...props,
            id,
            ...dismissible && {
                onClick: dismissOnClick,
                onKeyDown: dismissOnKeyDown
            }
        }), {
            elementType: dismissible ? 'button' : 'span'
        }),
        media: slot.optional(props.media, {
            elementType: 'span'
        }),
        icon: slot.optional(props.icon, {
            elementType: 'span'
        }),
        primaryText: slot.optional(props.primaryText, {
            renderByDefault: true,
            defaultProps: {
                children: props.children
            },
            elementType: 'span'
        }),
        secondaryText: slot.optional(props.secondaryText, {
            elementType: 'span'
        }),
        dismissIcon: slot.optional(props.dismissIcon, {
            renderByDefault: dismissible,
            defaultProps: {
                children: /*#__PURE__*/ React.createElement(DismissRegular, null),
                role: 'img'
            },
            elementType: 'span'
        })
    };
};
